// Relative, not aliased: this module is imported by node specs that run
// without the bundler's aliases.
import { orgEstimateScale, orgLocale } from './orgPreferences';

/**
 * How a length of work is written down, and what it is worth in minutes.
 *
 * The mirror of `app/Support/Duration.php` — same grammar, same answers, held
 * to the same table of cases (`resources/data/duration-cases.json`) by
 * `tests/e2e/unit/duration.spec.js`. It exists twice because the parsing has to
 * happen where somebody types, so the field can show what it understood before
 * the form is submitted, *and* at the endpoint, so a duration is a duration
 * whether it arrives from this field, an import or an API client.
 *
 * Minutes stay the stored unit — `tasks.estimated_minutes` is an integer and an
 * integer cannot drift. What minutes are bad at is being typed: asking for 960
 * when somebody means two days is asking them to do the arithmetic the machine
 * is for. So the grammar goes in front of the column: « 2h30 », « 3,5h », « 2j »,
 * « 1j 4h », « 90m ».
 *
 * A bare number — « 2 » — is read as **hours**. This application shows
 * estimates in hours, logs time in hours and bills in hours; nobody typing 2
 * into an estimate field means two minutes. It is the opposite of what Jira
 * does, which is why {@see formatDuration} is called on blur: « 2 » becomes
 * « 2 h » under the cursor, and a Jira habit corrects itself in one keystroke
 * rather than shipping a 30× error into a projection.
 *
 * This is a leaf — imported by forms, grids and node specs that run without the
 * bundler's aliases — so it reads the organization's day and week from
 * {@see orgEstimateScale}, which the org store pushes in. It never reaches for a
 * store itself.
 */

export const DEFAULT_HOURS_PER_DAY = 8;
export const DEFAULT_DAYS_PER_WEEK = 5;

/**
 * Ten years. Not a storage limit but a typo limit: « 2000j » is a slip, and a
 * phase roll-up carrying it reads as a planning catastrophe rather than a
 * fat-fingered field.
 */
export const MAX_DURATION_MINUTES = 5256000;

const UNITS = {
    minute: ['m', 'min', 'mn', 'mins', 'minute', 'minutes'],
    hour: ['h', 'hr', 'hrs', 'hour', 'hours', 'heure', 'heures'],
    day: ['d', 'j', 'day', 'days', 'jour', 'jours'],
    week: ['w', 's', 'wk', 'week', 'weeks', 'sem', 'semaine', 'semaines']
};

/** Thrown for a text that is not a duration. Empty is not an error. */
export class InvalidDurationError extends Error {}

function scale({ hoursPerDay, daysPerWeek } = {}) {
    const fromOrg = orgEstimateScale();

    return {
        hoursPerDay: positiveOr(hoursPerDay, fromOrg.hoursPerDay),
        daysPerWeek: positiveOr(daysPerWeek, fromOrg.daysPerWeek)
    };
}

function positiveOr(value, fallback) {
    const number = Number(value);

    return Number.isFinite(number) && number > 0 ? number : fallback;
}

function minutesPerDayFrom(hoursPerDay) {
    return Math.round(Math.max(hoursPerDay, 0.1) * 60);
}

/** Lowercase, decimal commas turned into points, and the thin spaces a paste carries. */
function normalize(text) {
    return String(text).replace(/[   ]/g, ' ').toLowerCase().replace(/,/g, '.');
}

function unitOf(suffix, text) {
    if (suffix === '') return null;

    const found = Object.keys(UNITS).find(unit => UNITS[unit].includes(suffix));

    if (!found) {
        throw new InvalidDurationError(`Unknown duration unit « ${suffix} » in: ${text}`);
    }

    return found;
}

/**
 * The text cut into `[amount, unit]` pairs, the unit null when the number
 * carried none. A tail the pattern cannot reach — « 2h plus tard » — is a
 * sentence, not a duration.
 */
function segmentsOf(text) {
    const pattern = /\s*(\d+(?:\.\d+)?)\s*([a-z]*)\s*/g;
    const segments = [];
    let consumed = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
        // Anchored end to end: an unreachable position means the text is not
        // only made of duration segments.
        if (match.index !== consumed) break;

        consumed += match[0].length;
        segments.push([parseFloat(match[1]), unitOf(match[2], text)]);
    }

    if (!segments.length || consumed !== text.length) {
        throw new InvalidDurationError(`Not a duration: ${text}`);
    }

    return segments;
}

/**
 * Minutes for what was typed, or null when nothing was.
 *
 * @param {string|number|null} input
 * @param {{hoursPerDay?: number, daysPerWeek?: number}} [options] the
 *   organization's own day and week; omitted, they are read from the org store's
 *   pushed-in preferences.
 * @returns {?number}
 * @throws {InvalidDurationError} when the text is not a duration at all
 */
export function parseDuration(input, options) {
    // A number arriving from an older client, or from the column itself, is
    // already minutes — the grammar is for text.
    if (typeof input === 'number') {
        return guard(Math.round(input));
    }

    const text = normalize(input ?? '').trim();

    if (text === '') return null;

    const { hoursPerDay, daysPerWeek } = scale(options);
    const minutesPerDay = minutesPerDayFrom(hoursPerDay);
    const minutesPerWeek = Math.round(minutesPerDay * Math.max(daysPerWeek, 0.1));

    const segments = segmentsOf(text);
    const perUnit = { minute: 1, hour: 60, day: minutesPerDay, week: minutesPerWeek };

    let total = 0;

    segments.forEach(([amount, unit], index) => {
        if (unit === null) {
            // « 2h30 »: a bare number trailing an hour segment is its minutes,
            // the way a clock is read. Standing alone it is hours.
            const trailsHours = index > 0 && segments[index - 1][1] === 'hour';

            total += trailsHours ? amount : amount * 60;

            return;
        }

        total += amount * perUnit[unit];
    });

    // « 1j 2h 3h » is a slip, not a sum of 1 day and 5 hours — but only a
    // repeated *unit* betrays it, since « 2h30 » repeats nothing.
    const units = segments.map(([, unit]) => unit).filter(Boolean);

    if (units.length !== new Set(units).size) {
        throw new InvalidDurationError(`Not a duration: ${text}`);
    }

    return guard(Math.round(total));
}

/**
 * The same answer as {@see parseDuration}, with an unreadable text treated as
 * nothing typed. For a payload nobody should be able to reject — never for a
 * field a person filled in, which has to be told it was not understood.
 */
export function parseDurationOrNull(input, options) {
    try {
        return parseDuration(input, options);
    } catch (e) {
        if (e instanceof InvalidDurationError) return null;

        throw e;
    }
}

/** Whether the text is a duration this grammar accepts. Empty is not an error. */
export function isValidDuration(input, options) {
    try {
        parseDuration(input, options);

        return true;
    } catch (e) {
        if (e instanceof InvalidDurationError) return false;

        throw e;
    }
}

/**
 * Minutes written the way the grammar would accept them back — « 2 h 30 »,
 * « 45 min », « 1 j 4 h ». **For a planned length**: an estimate, a budget, a
 * phase roll-up.
 *
 * Weeks are never written out even though they are read in: « 2s » comes back as
 * « 10 j », because a week is the unit people disagree about most and a number of
 * days is the same length whoever reads it. Which is also why this needs no
 * `daysPerWeek`.
 *
 * @returns {?string}
 */
export function formatDuration(minutes, options) {
    const value = round(minutes);

    if (value === null) return null;
    if (value <= 0) return '0 h';

    const minutesPerDay = minutesPerDayFrom(scale(options).hoursPerDay);

    return write(Math.floor(value / minutesPerDay), value % minutesPerDay);
}

/**
 * The same minutes written **without ever grouping into days** — « 8 h »,
 * « 2 h 30 », « 45 min », « -3 h 15 ». For a *measured* length: a timesheet row,
 * a day's total, a time-bank balance, the hours logged against a task.
 *
 * Two reasons this is not {@see formatDuration}:
 *
 *  - **A day is a planning convention, not a measurement.** Eight hours clocked
 *    on a Tuesday are eight hours; writing them « 1 j » answers a question about
 *    capacity that a timesheet was not asked. Worse, it moves: the same 480
 *    minutes would read « 1 j » for one organization and « 1 j 1 h » for the next.
 *    A payroll figure must not depend on a planning setting.
 *  - **A measured length can be negative.** A time bank runs below zero, and a
 *    period can be short of its expected hours. `formatDuration` writes « 0 h »
 *    for anything at or under zero, which is right for an estimate and silently
 *    destroys a debt.
 *
 * Nothing measured reads « 0 h » rather than blank, which is the third
 * difference: for a measurement « nothing logged » and « zero logged » are the
 * same fact, where an estimate has to tell « not estimated » from « estimated at
 * nothing ». A total that has not loaded yet therefore shows a zero, not an
 * empty cell that looks like a broken widget.
 *
 * @returns {string}
 */
export function formatHours(minutes) {
    const value = round(minutes) ?? 0;

    if (value === 0) return '0 h';

    const sign = value < 0 ? '-' : '';

    return sign + write(0, Math.abs(value));
}

/**
 * The same minutes as a **decimal number of hours**, the number alone — « 28 »,
 * « 15,5 », « 0,5 ». For a figure meant to be compared or added up by eye: a
 * stat tile against its budget, the « 4,5 / 12 h » a row shows.
 *
 * « 15 h 30 » and « 12 h » do not compare at a glance; 15,5 and 12 do. That is
 * the whole reason this rendering exists, and the reason it is not
 * {@see formatHours} with a different separator.
 *
 * No unit, because its two callers carry their own: a stat tile puts the « h »
 * in a span of its own size, and a ratio writes one « h » for two figures.
 *
 * The decimal mark follows the reader's locale, so an English organization gets
 * « 15.5 » — the copies this replaces wrote a French comma whatever the reader.
 *
 * @returns {string}
 */
export function decimalHours(minutes) {
    const rounded = Math.round(((round(minutes) ?? 0) / 60) * 10) / 10;

    return new Intl.NumberFormat(orgLocale(), { maximumFractionDigits: 1 }).format(rounded);
}

/** Minutes as a whole number, or null when there is nothing to write. */
function round(minutes) {
    if (minutes === null || minutes === undefined || minutes === '') return null;

    const value = Math.round(Number(minutes));

    return Number.isFinite(value) ? value : null;
}

/** The one place the words are chosen, so the two renderings cannot drift. */
function write(days, restMinutes) {
    const hours = Math.floor(restMinutes / 60);
    const mins = restMinutes % 60;

    const parts = [];

    if (days > 0) parts.push(`${days} j`);
    if (hours > 0) parts.push(`${hours} h`);

    if (mins > 0) {
        // « 2 h 05 » reads as a clock, two digits and all; « 30 min » standing
        // alone needs its unit, or it reads as 30 of whatever came before.
        parts.push(hours > 0 ? String(mins).padStart(2, '0') : `${mins} min`);
    }

    return parts.join(' ');
}

function guard(minutes) {
    if (!Number.isFinite(minutes) || minutes < 0) {
        throw new InvalidDurationError('A duration cannot be negative.');
    }

    if (minutes > MAX_DURATION_MINUTES) {
        throw new InvalidDurationError('That duration is longer than ten years.');
    }

    return minutes;
}
